import { useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { VscEye } from "react-icons/vsc";
import { Tooltip } from "antd";
import { Pencil, X, Info } from "lucide-react";
import moment from "moment";

import { CommonImages, ProfileImages } from "../../../../reusableUI/images";
import Loader from "../../../../reusableUI/loader";
import ReusableTable from "../../../../reusableUI/table";
import Page from "../../../../reusableUI/page";

import { capitalizeFirstLetter } from "../../../../utils/Functions";
import {
  getUnassignedHCPNames,
  assignHCP,

  getBookingsDetails,
} from "../../../../Service/bookings";
import { getHospitalList } from "../../../../Service/RM";

const SortIcon = ({ isSorted, isSortedDesc }) => {
  if (!isSorted) {
    return (
      <img
        src={ProfileImages.filter}
        alt="filter"
        className="h-3 w-2 ml-1 cursor-pointer opacity-60"
      />
    );
  }

  if (isSortedDesc) {
    return (
      <svg
        className="h-3 w-3 ml-1 text-blue-600"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M5 8l5 5 5-5H5z" />
      </svg>
    );
  }

  return (
    <svg
      className="h-3 w-3 ml-1 text-blue-600"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path d="M15 12l-5-5-5 5h10z" />
    </svg>
  );
};

const UnassignedBookings = ({ searchTerm }) => {
  const [loading, setLoading] = useState(true);
  const [paginationPage, setPaginationPage] = useState(0);
  const [unassignedList, setUnassignedList] = useState([]);
  const [hcpNames, setUnassignedHCPNames] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedHospitalForModal, setSelectedHospitalForModal] = useState("");
  const [selectedPageCount, setSelectedPageCount] = useState(() => {
    const saved = localStorage.getItem("pageCount");
    return saved ? parseInt(saved, 10) : 50;
  });
  const [sortBy, setSortBy] = useState(() => {
    const saved = localStorage.getItem("sortBy");
    return saved ? JSON.parse(saved) : [];
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const result = await getBookingsDetails();
      console.log("Unassigned Bookings fetched:", result);
      const bookingsData = Array.isArray(result?.data?.bookings)
        ? result.data.bookings
        : [];
      const unassignedBookings = bookingsData.filter(
        (booking) => booking?.assigned === false && booking?.status === "pending" && booking?.appointmentType?.type === "self"
      );
      setUnassignedList(unassignedBookings);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setUnassignedList([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch HCP names
  useEffect(() => {
    const fetchHCPNames = async () => {
      try {
        const response = await getHospitalList();
        console.log(response)
        if (response?.data && Array.isArray(response.data)) {
          setUnassignedHCPNames(response.data);
        } else if (Array.isArray(response)) {
          setUnassignedHCPNames(response);
        } else {
          setUnassignedHCPNames([]);
        }
      } catch (error) {
        console.error("Error fetching Hospital names:", error);
        setUnassignedHCPNames([]);
      }
    };
    fetchHCPNames();
  }, []);

  // Fetch bookings on component mount

  const handleAssignChange = async (bookingId, hospitalName) => {
    const selectedHCP = hcpNames.find(
      (hcp) => hcp?.basicInfo?.name === hospitalName
    );
    if (!selectedHCP) return;

    const payload = {
      visitID: bookingId,
      partnerInfo: {
        id: selectedHCP._id,

        name: selectedHCP.basicInfo?.name,

      },
    };

    console.log("Assigning Partner:", payload);

    try {
      setLoading(true);
      const response = await assignHCP(payload);
      console.log(response)
      if (response?.error === false) {
        fetchBookings();
        setUnassignedList((prev) =>
          prev.map((item) =>
            item._id === bookingId
              ? { ...item, assignedTo: hospitalName, assigned: true }
              : item
          )
        );
      }
    } catch (error) {
      console.error("Failed to assign HCP:", error);
    } finally {
      setLoading(false);
    }
  };

  const navigateToProfile = (index, original) => {
    localStorage.setItem("profilePage", "user");
    navigate(`/admin/bookingsProfile/${original._id}`, {
      state: { user: original, card: "2" },
    });
  };

  // Filtered data
  const filteredData = useMemo(() => {
    if (!searchTerm) return unassignedList;
    return unassignedList.filter((item) =>
      item?.patient?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [unassignedList, searchTerm]);

  const calculateAge = (dob) => {
    if (!dob) return "-";
    const age = moment().diff(moment(dob), "years");
    return `${age} yrs`;
  };

  // Table columns
  const columns = useMemo(() => {
    return [
      {
        Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Name</div>,
        accessor: "patient.name",
        id: "name",
        width: 200,
        minWidth: 180,
        maxWidth: 240,
        Cell: ({ row: { original } }) => (
          <div className="flex flex-row items-center gap-1.5 whitespace-nowrap ml-2">
            <div className="relative inline-block shrink-0">
              <img
                src={
                  original?.patient?.gender?.toLowerCase() === "female"
                    ? CommonImages.female
                    : original?.patient?.gender?.toLowerCase() === "male"
                      ? CommonImages.male
                      : CommonImages.defaultDp
                }
                alt="Profile"
                className="h-8 w-8 rounded-full object-cover"
              />
            </div>
            <span className="text-[#4361EE] font-poppins text-[13px] font-medium">
              {capitalizeFirstLetter(original?.patient?.name || "N/A")}
            </span>
          </div>
        ),
      },
      {
        Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Date</div>,
        accessor: "visitInfo",
        width: 170,
        minWidth: 150,
        maxWidth: 200,
        Cell: ({ row: { original } }) => {
          const start = original?.visitInfo?.startDate ? moment(original.visitInfo.startDate).format("DD/MM/YYYY") : "";
          const end = original?.visitInfo?.endDate ? moment(original.visitInfo.endDate).format("DD/MM/YYYY") : "";
          const days = parseInt(original?.visitInfo?.days) || 0;

          let dateText = "-";
          if (days === 1 || start === end) {
            dateText = start;
          } else if (start && end) {
            dateText = `${start} - ${end}`;
          } else if (start || end) {
            dateText = start || end;
          }

          return (
            <div className="font-poppins text-[13px] text-gray-700 whitespace-nowrap">
              {dateText}
            </div>
          );
        },
      },
      {
        Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Time</div>,
        accessor: "visitInfo.slot",
        width: 160,
        minWidth: 140,
        maxWidth: 190,
        Cell: ({ row: { original } }) => {
          const timeText = original?.visitInfo?.slot || "-";
          return (
            <div className="font-poppins text-[13px] text-gray-700 whitespace-nowrap">
              {timeText}
            </div>
          );
        },
      },
      {
        Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Gender</div>,
        accessor: "patient.gender",
        width: 90,
        minWidth: 80,
        maxWidth: 110,
        Cell: ({ row: { original } }) => (
          <div className="font-poppins text-[13px] text-gray-700">
            {capitalizeFirstLetter(original?.patient?.gender || "-")}
          </div>
        ),
      },
      {
        Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Age</div>,
        accessor: "patient.dob",
        width: 80,
        minWidth: 70,
        maxWidth: 100,
        Cell: ({ row: { original } }) => (
          <div className="font-poppins text-[13px] text-gray-700 whitespace-nowrap">
            {calculateAge(original?.patientID?.personalInfo?.basicInfo?.otherInfo?.dob || null)}
          </div>
        ),
      },
      {
        Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Service</div>,
        accessor: "specialistInfo.role",
        width: 140,
        minWidth: 120,
        maxWidth: 160,
        Cell: ({ row: { original } }) => {
          let roleStr = capitalizeFirstLetter(original?.specialistInfo?.role || "-");
          if (roleStr.toLowerCase() === "physio" || roleStr.toLowerCase() === "physiotherapist") {
            roleStr = "Physiotherapist";
          }
          return (
            <div className="font-poppins text-[13px] text-gray-700 truncate max-w-[130px]">
              {roleStr}
            </div>
          );
        },
      },
      {
        Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Location</div>,
        accessor: "visitInfo.addressInfo.district",
        width: 200,
        minWidth: 180,
        maxWidth: 250,
        Cell: ({ row: { original } }) => {
          // const addr1 = original?.visitInfo?.addressInfo?.address1 || original?.visitInfo?.addressInfo?.district || "";
          const city = original?.visitInfo?.addressInfo?.city || "";
          const loc = [city].filter(Boolean).map(capitalizeFirstLetter).join(", ");
          return (
            <div className="font-poppins text-[13px] text-gray-700 whitespace-normal break-words max-w-[230px]" title={loc || "-"}>
              {loc || "-"}
            </div>
          );
        },
      },
      {
        Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Assign</div>,
        accessor: "assignedTo",
        width: 200,
        minWidth: 180,
        maxWidth: 220,
        Cell: ({ row: { original } }) => {
          let displayAssignee = "Not Assigned";
          if (original?.appointmentType?.type === "partner") {
            displayAssignee = original?.appointmentType?.partnerName || original?.assignedTo || "Not Assigned";
          } else if (original?.appointmentType?.type === "self") {
            displayAssignee = original?.specialistInfo?.name || original?.assignedTo || "Not Assigned";
          } else {
            displayAssignee = original?.assignedTo || "Not Assigned";
          }

          return (
            <div className="font-poppins text-[13px] flex items-center gap-1.5 min-w-[170px]">
              <span className={`truncate max-w-[130px] ${displayAssignee !== "Not Assigned" ? "text-[#4361EE] font-medium" : "text-gray-500"}`}>
                {displayAssignee}
              </span>
              {displayAssignee !== "Not Assigned" && displayAssignee.length > 10 && (
                <Tooltip title={displayAssignee} placement="top">
                  <div className="cursor-pointer text-gray-400 hover:text-blue-600 shrink-0 mt-0.5">
                    <Info size={14} />
                  </div>
                </Tooltip>
              )}
              {!original.assigned && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingRowId(original._id);
                    setSelectedHospitalForModal(original.assignedTo || "");
                    setIsModalOpen(true);
                  }}
                  className="p-1 rounded-full hover:bg-blue-50 shrink-0 ml-1"
                  title="Assign Partner"
                >
                  <Pencil size={14} className="text-[#4361EE]" />
                </button>
              )}
            </div>
          );
        },
      },
      {
        Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Profile</div>,
        accessor: "view",
        width: 80,
        minWidth: 70,
        maxWidth: 90,
        Cell: ({ row: { original } }) => (
          <div className="flex flex-row items-center">
            <button
              onClick={() => navigateToProfile(null, original)}
              className="text-[#4361EE] underline font-medium font-poppins text-[13px] hover:opacity-80"
            >
              View
            </button>
          </div>
        ),
      },
    ];
  }, [hcpNames, paginationPage, selectedPageCount, editingRowId]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page: tablePage,
    prepareRow,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    state: { sortBy: currentSortBy },
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: {
        pageSize: selectedPageCount,
        pageIndex: paginationPage,
        sortBy: sortBy.length > 0 ? sortBy : [],
      },
    },
    useSortBy,
    usePagination
  );

  useEffect(() => {
    localStorage.setItem("sortBy", JSON.stringify(currentSortBy));
  }, [currentSortBy]);

  return (
    <>
      {loading ? (
        <Loader loading={loading} />
      ) : (
        <div className="bg-[#F3F6F5]">
          <div className="overflow-x-auto bg-white rounded-2xl shadow-lg p-2 h-[89vh] m-2 flex flex-col gap-3 justify-between">
            <div className="flex justify-between items-center">
              <p className="text-[14px] ml-2 font-bold pb-2 p-2">
                Unassigned Bookings ({filteredData.length})
              </p>
            </div>

            <div className="overflow-x-auto min-h-[75vh] max-h-[75vh] mt-[-10px]">
              <div className="min-w-[1200px] w-full h-full">
                <table {...getTableProps()} className="w-full">
                  <thead className="bg-[#F6F8FC] sticky top-0 z-10">
                    {headerGroups.map((headerGroup) => (
                      <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <th
                            key={column.id}
                            {...column.getHeaderProps(column.getSortByToggleProps())}
                            className="px-2 py-1.5 text-left text-[12px] font-medium text-[#8177A8] tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                          >
                            <div className="flex items-center gap-1">
                              {column.render("Header")}
                              {column.canSort && (
                                <SortIcon isSorted={column.isSorted} isSortedDesc={column.isSortedDesc} />
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
                    {tablePage.map((row) => {
                      prepareRow(row);
                      return (
                        <tr
                          key={row.id}
                          {...row.getRowProps()}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          {row.cells.map((cell) => (
                            <td
                              key={cell.column.id}
                              {...cell.getCellProps()}
                              className="px-2 py-1.5 text-[12px] text-gray-700 whitespace-nowrap"
                            >
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                    {tablePage.length === 0 && (
                      <tr>
                        <td colSpan={columns.length} className="text-center py-10 text-gray-500 text-sm">
                          No unassigned bookings found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-auto mb-2">
              <Page
                paginationPage={paginationPage}
                nextPage={nextPage}
                previousPage={previousPage}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageOptions={pageOptions}
                screen="unassignedBookings"
              />
            </div>
          </div>
        </div>
      )}
      {/* Assign Partner Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-[16px] font-semibold text-[#4951EC] font-poppins">Assign Partner</h3>
              <button
                onClick={() => { setIsModalOpen(false); setEditingRowId(null); }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-6">
              <label className="block text-[13px] font-medium text-gray-700 font-poppins mb-2">
                Select Partner
              </label>
              <select
                className="w-full bg-[#F6F8FC] rounded-md px-3 py-2.5 text-[13px] font-poppins border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#4361EE] focus:border-transparent transition-all"
                value={selectedHospitalForModal}
                onChange={(e) => setSelectedHospitalForModal(e.target.value)}
              >
                <option value="" disabled hidden>Select a partner / hospital</option>
                {hcpNames.filter((hcp) => hcp.active !== false).map((hospital) => (
                  <option key={hospital._id} value={hospital?.basicInfo?.name || "N/A"}>
                    {hospital?.basicInfo?.name || "N/A"}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
              <button
                onClick={() => { setIsModalOpen(false); setEditingRowId(null); }}
                className="px-4 py-2 text-[13px] font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-poppins transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedHospitalForModal && editingRowId) {
                    const selectedHcp = hcpNames.find(h => h.basicInfo?.name === selectedHospitalForModal);
                    handleAssignChange(editingRowId, selectedHospitalForModal, selectedHcp);
                    setIsModalOpen(false);
                    setEditingRowId(null);
                  }
                }}
                disabled={!selectedHospitalForModal}
                className="px-5 py-2 text-[13px] font-medium text-white bg-[#4361EE] hover:bg-blue-700 rounded-md disabled:opacity-50 font-poppins transition-colors"
              >
                Confirm Assignment
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UnassignedBookings;
